import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import './AddAssetForm.css'; // Import CSS for styling
import { addAsset } from '../../store/assetSlice'; // Import the addAsset action
import parseDateToUtcIso from '../../utils/date';
import {
  ACCOUNTING_DEPRECIATION_METHOD_OPTIONS,
  TAX_ASSET_BLOCK_OPTIONS,
} from '../../constants/taxOptions';

const AssetForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate(); // Hook for navigation

  // Select clients from Redux state
  const { clientId } = useSelector((state) => state.app);
  const [assetName, setAssetName] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [annualDepreciation, setAnnualDepreciation] = useState('');
  const [purchaseYear, setPurchaseYear] = useState('');
  const [putToUseDate, setPutToUseDate] = useState('');
  const [salvageValue, setSalvageValue] = useState('0');
  const [usefulLife, setUsefulLife] = useState('');
  const [accountingDepreciationMethod, setAccountingDepreciationMethod] = useState('WRITTEN_DOWN_VALUE');
  const [taxAssetBlock, setTaxAssetBlock] = useState('');
  const [taxDepreciationRate, setTaxDepreciationRate] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();

    const requiresAccountingRate = accountingDepreciationMethod === 'WRITTEN_DOWN_VALUE';
    const requiresUsefulLife = accountingDepreciationMethod === 'STRAIGHT_LINE';

    if (!assetName || !purchasePrice || !purchaseYear || !putToUseDate || !accountingDepreciationMethod || !taxAssetBlock) {
      setError('Please fill in all required asset fields.');
      return;
    }

    if (requiresAccountingRate && !annualDepreciation) {
      setError('Please provide the accounting WDV rate for the selected accounting method.');
      return;
    }

    if (requiresUsefulLife && !usefulLife) {
      setError('Please provide useful life when Straight Line Method is selected.');
      return;
    }

    const assetData = {
      clientId: clientId,
      assetName,
      purchasePrice: parseFloat(purchasePrice),
      annualDepreciation: annualDepreciation ? parseFloat(annualDepreciation) : null,
      purchaseYear: parseInt(purchaseYear, 10),
      putToUseDate: parseDateToUtcIso(putToUseDate),
      salvageValue: salvageValue ? parseFloat(salvageValue) : 0,
      usefulLife: usefulLife ? parseInt(usefulLife, 10) : 0,
      accountingDepreciationMethod,
      taxAssetBlock,
      taxDepreciationRate: taxDepreciationRate ? parseFloat(taxDepreciationRate) : null,
    };

    dispatch(addAsset(assetData))
      .unwrap()
      .then(() => {
        setSuccess('Asset details saved successfully!');
        setError('');
        // Clear form fields
        setAssetName('');
        setPurchasePrice('');
        setPurchaseYear('');
        setPutToUseDate('');
        setSalvageValue('0');
        setUsefulLife('');
        setAnnualDepreciation('');
        setAccountingDepreciationMethod('WRITTEN_DOWN_VALUE');
        setTaxAssetBlock('');
        setTaxDepreciationRate('');
        navigate('/asset-list');
      })
      .catch((err) => {
        setError('Error saving asset details.');
        setSuccess('');
      });
  };

  return (
    <div className="asset-form-page">
      <h2>Asset Details Form</h2>
      <p className="financial-page-subtitle">Capture purchase, accounting depreciation, and tax block inputs together so reporting stays aligned.</p>
      <form onSubmit={handleSubmit} className="asset-form">
        
        <div className="form-group">
          <label htmlFor="assetName">Asset Name:</label>
          <input
            type="text"
            id="assetName"
            value={assetName}
            onChange={(e) => setAssetName(e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="purchasePrice">Purchase Price:</label>
          <input
            type="number"
            id="purchasePrice"
            value={purchasePrice}
            onChange={(e) => setPurchasePrice(e.target.value)}
            step="0.01"
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="purchaseYear">Purchase FY Start Year:</label>
          <input
            type="number"
            id="purchaseYear"
            value={purchaseYear}
            onChange={(e) => setPurchaseYear(e.target.value)}
            placeholder="2024"
            required
          />
          <small>Use the first year of the financial year. Example: FY 2024-2025 means `2024`.</small>
        </div>
        <div className="form-group">
          <label htmlFor="putToUseDate">Put To Use Date:</label>
          <input
            type="date"
            id="putToUseDate"
            value={putToUseDate}
            onChange={(e) => setPutToUseDate(e.target.value)}
            required
          />
          <small>This date drives the tax 180-day rule in the first eligible year.</small>
        </div>
        <div className="form-section-title">Accounting Inputs</div>
        <div className="form-group">
          <label htmlFor="accountingDepreciationMethod">Accounting Depreciation Method:</label>
          <select
            id="accountingDepreciationMethod"
            value={accountingDepreciationMethod}
            onChange={(e) => setAccountingDepreciationMethod(e.target.value)}
            required
          >
            {ACCOUNTING_DEPRECIATION_METHOD_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="annualDepreciation">Accounting WDV Rate %:</label>
          <input
            type="number"
            id="annualDepreciation"
            value={annualDepreciation}
            onChange={(e) => setAnnualDepreciation(e.target.value)}
            step="0.01"
            required={accountingDepreciationMethod === 'WRITTEN_DOWN_VALUE'}
          />
          <small>Required for WDV accounting. Optional for SLM.</small>
        </div>
        <div className="form-group">
          <label htmlFor="salvageValue">Salvage Value:</label>
          <input
            type="number"
            id="salvageValue"
            value={salvageValue}
            onChange={(e) => setSalvageValue(e.target.value)}
            step="0.01"
          />
        </div>
        <div className="form-group">
          <label htmlFor="usefulLife">Useful Life (years):</label>
          <input
            type="number"
            id="usefulLife"
            value={usefulLife}
            onChange={(e) => setUsefulLife(e.target.value)}
            required={accountingDepreciationMethod === 'STRAIGHT_LINE'}
          />
          <small>Required for Straight Line Method.</small>
        </div>
        <div className="form-section-title">Tax Inputs</div>
        <div className="form-group">
          <label htmlFor="taxAssetBlock">Tax Asset Block:</label>
          <select
            id="taxAssetBlock"
            value={taxAssetBlock}
            onChange={(e) => setTaxAssetBlock(e.target.value)}
            required
          >
            <option value="">Select tax block</option>
            {TAX_ASSET_BLOCK_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="taxDepreciationRate">Tax Rate Override %:</label>
          <input
            type="number"
            id="taxDepreciationRate"
            value={taxDepreciationRate}
            onChange={(e) => setTaxDepreciationRate(e.target.value)}
            step="0.01"
          />
          <small>Optional. Leave blank to use the default rate from the selected tax block.</small>
        </div>
        {error && <p className="error-message financial-message financial-message--error">{error}</p>}
        {success && <p className="success-message financial-message financial-message--success">{success}</p>}
        <button type="submit" className="submit-button financial-button">Save Asset</button>
      </form>
    </div>
  );
};

export default AssetForm;
